import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Popup from "../../../components/Popup";
import {
  EmptyState,
  IconButton,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  TableSkeleton,
  usePagination,
} from "../../../components/ui";
import { addFAQ, deleteFAQ, getFAQList, updateFAQ } from "../../../services/faq";
import { matches } from "../../../utils/list";
import SectionCard from "../legal/SectionCard";
import "./faqs.css";

const EMPTY_FAQ = { question: "", answer: "" };

const FAQS = () => {
  const { t } = useTranslation();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Form: the FAQ being added ({ question, answer }) or edited (with its _id).
  const [form, setForm] = useState(EMPTY_FAQ);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const refresh = () =>
    getFAQList().then((data) => setFaqs(data?.faqList || []));
  useEffect(() => {
    refresh().then(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () =>
      faqs.filter(
        (faq) => matches(faq.question, search) || matches(faq.answer, search)
      ),
    [faqs, search]
  );
  const pagination = usePagination(filtered, 10, [search]);

  const setField = (field) => (e) => {
    const { value } = e.target;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error(t("Please Fill FAQ Data Completely!"));
      return;
    }
    const body = { question: form.question, answer: form.answer };
    setSaving(true);
    const saved = form._id
      ? await updateFAQ(body, form._id)
      : await addFAQ(body).catch(() => false);
    setSaving(false);
    if (!saved) {
      toast.error(t("Adding FAQ Failed!"));
      return;
    }
    toast.success(t(form._id ? "FAQs Updated Successfully !" : "FAQ Added Successfully!"));
    setForm(EMPTY_FAQ);
    refresh();
  };

  const handleDelete = async (faq) => {
    try {
      await deleteFAQ(faq._id);
      toast.success(t("FAQ Deleted Successfully!"));
      if (form._id === faq._id) setForm(EMPTY_FAQ);
      refresh();
    } catch (error) {
      toast.error(t("Error In Deleting FAQ"));
    }
  };

  return (
    <Page>
      <PageHeader
        title={t("FAQ")}
        subtitle={loading ? " " : t("faqCount", { count: faqs.length })}
      />

      <SectionCard
        title={form._id ? t("Edit FAQ") : t("Add FAQ")}
        editing
        saving={saving}
        onCancel={form._id || form.question || form.answer ? () => setForm(EMPTY_FAQ) : undefined}
        onSave={handleSave}
      >
        <div className="ui-form faq-form">
          <div className="ui-field ui-field-full">
            <label htmlFor="faq-question">{t("Question")}</label>
            <input
              id="faq-question"
              className="ui-input"
              value={form.question}
              onChange={setField("question")}
            />
          </div>
          <div className="ui-field ui-field-full">
            <label htmlFor="faq-answer">{t("Answer")}</label>
            <textarea
              id="faq-answer"
              className="ui-input faq-answer"
              rows={4}
              value={form.answer}
              onChange={setField("answer")}
            />
          </div>
        </div>
      </SectionCard>

      <section className="ui-card">
        <div className="ui-toolbar">
          <h2 className="faq-list-title">{t("FAQS List")}</h2>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("Search a question")}
          />
        </div>
        {loading ? (
          <TableSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-question-answer-line">{t("No Data Found")}</EmptyState>
        ) : (
          <>
            <ul className="faq-list">
              {pagination.pageItems.map((faq) => (
                <li
                  key={faq._id}
                  className={form._id === faq._id ? "is-selected" : undefined}
                >
                  <details>
                    <summary>
                      <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                      <span>{faq.question}</span>
                    </summary>
                    <p>{faq.answer}</p>
                  </details>
                  <div className="faq-actions">
                    <IconButton
                      icon="ri-pencil-line"
                      label={t("Edit")}
                      onClick={() => {
                        setForm({ _id: faq._id, question: faq.question, answer: faq.answer });
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    />
                    <IconButton
                      icon="ri-delete-bin-line"
                      label={t("Delete")}
                      tone="danger"
                      onClick={() => setToDelete(faq)}
                    />
                  </div>
                </li>
              ))}
            </ul>
            <Pagination {...pagination} onChange={pagination.setPage} />
          </>
        )}
      </section>

      {toDelete && (
        <Popup
          open={true}
          setOpen={() => setToDelete(null)}
          deleteHandle={() => handleDelete(toDelete)}
        >
          <div className="model-wrapper">
            {t("Are you sure, you want to delete this FAQ?")}
          </div>
        </Popup>
      )}
    </Page>
  );
};

export default FAQS;
